import { SenderDto, SendingSettingsDto } from "@growth/dto";
import { Badge, Button, cn, Field, Input, Textarea } from "@growth/ui";
import { Check, LoaderCircle, Mail, PenLine, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { ErrorText, PageHeader, selectClass } from "../../components/page-header";
import { useActive } from "../../lib/active";
import { apiErrorMessage } from "../../lib/api-error";
import { formatDate } from "../../lib/format";
import {
  useSaveSender,
  useSaveSendingSettings,
  useSendingSettings,
  useSuppress,
  useUnsuppress,
} from "../../lib/leads";
import { card, EmailPreview, Panel, SampleBanner } from "./shared";

/** How email leaves the platform, who it's from, and what we count. */
export const SendingPage = () => {
  const { brand } = useActive();
  const settings = useSendingSettings();
  const data = settings.data;
  return (
    <section className="max-w-5xl space-y-6">
      <SampleBanner />
      <PageHeader eyebrow={`${brand?.name ?? ""} · Leads`} title="Sending">
        Who emails come from, how they leave the platform, and what counts as engagement. Every
        email still needs a person's approval.
      </PageHeader>
      {settings.isError && <ErrorText>{apiErrorMessage(settings.error)}</ErrorText>}
      {settings.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {data && (
        <>
          <Provider settings={data} />
          <Senders settings={data} />
          <Tracking settings={data} />
          <Suppression settings={data} />
        </>
      )}
    </section>
  );
};

const Provider = ({ settings }: { settings: SendingSettingsDto }) => (
  <Panel
    title="Sending provider"
    about="The service that delivers the email and reports bounces, opens and clicks back."
    actions={
      <Button disabled title="Arrives with the next phase">
        Connect Amazon SES
      </Button>
    }
  >
    <div className="flex flex-wrap items-center gap-3">
      <span className="inline-grid size-9 place-items-center rounded-lg bg-paper">
        <Mail className="size-4 text-ink" />
      </span>
      <div>
        <p className="text-sm font-medium text-ink">Amazon SES</p>
        <p className="text-xs text-muted">{settings.provider.detail}</p>
      </div>
      <Badge className="ml-auto bg-amber-50 text-amber-800 ring-amber-200">
        Not connected · test mode
      </Badge>
    </div>
    <ul className="list-disc space-y-1 pl-5 text-xs leading-relaxed text-muted">
      <li>
        Send from a separate AWS account, not the one that sends theResume's product email. SES
        reviews an account at a 5% bounce rate or a 0.1% complaint rate, and a pause stops every
        email from that account.
      </li>
      <li>
        AWS doesn't allow unsolicited bulk email. Cold emails go one at a time, only to addresses
        published for that role, after the checks pass. Newsletters go only to people who signed up.
      </li>
      <li>Bounces and complaints come back through SES notifications and update each person.</li>
    </ul>
  </Panel>
);

const AUTH = ({ label, state }: { label: string; state: "pass" | "missing" }) => (
  <Badge
    className={
      state === "pass"
        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
        : "bg-red-50 text-red-700 ring-red-200"
    }
  >
    {state === "pass" ? <Check className="size-3" /> : <X className="size-3" />} {label}
  </Badge>
);

const emptySender = (domain: string): SenderDto => ({
  id: "",
  name: "",
  title: "",
  email: "",
  signature: "",
  optOutLine:
    "Not the right person, or rather not hear from me? Reply “no thanks” and I won't email again.",
  dailyCap: 30,
  sentToday: 0,
  domain: { name: domain, spf: "pass", dkim: "pass", dmarc: "pass" },
});

const Senders = ({ settings }: { settings: SendingSettingsDto }) => {
  const { atLeast } = useActive();
  const [editing, setEditing] = useState<SenderDto | null>(null);
  const domain = settings.senders[0]?.domain.name ?? "";
  return (
    <Panel
      title="Senders"
      about="Real people at the brand's own domain. Cold email from a no-reply or a login address loses trust and inbox placement."
      actions={
        atLeast("OWNER") && !editing ? (
          <Button variant="secondary" onClick={() => setEditing(emptySender(domain))}>
            <Plus className="size-4" /> Add a sender
          </Button>
        ) : undefined
      }
    >
      {editing && <SenderForm sender={editing} onClose={() => setEditing(null)} />}
      {settings.senders.length === 0 && !editing && (
        <p className="text-sm text-muted">
          No senders yet. Add the person whose name goes on the emails.
        </p>
      )}
      <ul className="divide-y divide-line">
        {settings.senders.map((sender) => (
          <li
            key={sender.id}
            className="grid gap-3 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
          >
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-ink">{sender.name}</p>
                  <p className="text-xs text-muted">
                    {sender.title} · {sender.email}
                  </p>
                </div>
                {atLeast("OWNER") && (
                  <Button variant="ghost" className="h-8 px-2" onClick={() => setEditing(sender)}>
                    <PenLine className="size-4" /> Edit
                  </Button>
                )}
              </div>
              <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
                <ShieldCheck className="size-3.5" /> {sender.domain.name}
                <AUTH label="SPF" state={sender.domain.spf} />
                <AUTH label="DKIM" state={sender.domain.dkim} />
                <AUTH label="DMARC" state={sender.domain.dmarc} />
              </p>
              <p className="text-xs text-muted">
                Up to {sender.dailyCap} emails a day · {sender.sentToday} sent today
              </p>
            </div>
            <EmailPreview
              sender={sender}
              to="someone@example.com"
              subject="How the end of every email looks"
              body="…"
            />
          </li>
        ))}
      </ul>
    </Panel>
  );
};

const SenderForm = ({ sender, onClose }: { sender: SenderDto; onClose: () => void }) => {
  const save = useSaveSender();
  const [form, setForm] = useState(sender);
  const set = <K extends keyof SenderDto>(key: K, value: SenderDto[K]) =>
    setForm({ ...form, [key]: value });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(form, { onSuccess: onClose });
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg bg-paper/50 p-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Name">
          {(props) => (
            <Input
              {...props}
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          )}
        </Field>
        <Field label="Title">
          {(props) => (
            <Input {...props} value={form.title} onChange={(e) => set("title", e.target.value)} />
          )}
        </Field>
        <Field label="Email" hint="A real mailbox at the brand's domain">
          {(props) => (
            <Input
              {...props}
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Signature">
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={form.signature}
              onChange={(e) => set("signature", e.target.value)}
            />
          )}
        </Field>
        <Field label="Opt-out line" hint="Required by the Spam Act. Keep it short and human.">
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              required
              value={form.optOutLine}
              onChange={(e) => set("optOutLine", e.target.value)}
            />
          )}
        </Field>
      </div>
      <Field label="Daily limit" hint="Cold email: about 30–50 a day per mailbox." className="w-40">
        {(props) => (
          <Input
            {...props}
            type="number"
            min={1}
            max={200}
            value={form.dailyCap}
            onChange={(e) => set("dailyCap", Number(e.target.value) || 1)}
          />
        )}
      </Field>
      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save sender
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

const NumberOrBlank = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) => (
  <Field label={label} hint={value === null ? "Not agreed yet" : undefined}>
    {(props) => (
      <Input
        {...props}
        type="number"
        min={0}
        max={100}
        placeholder="–"
        value={value ?? ""}
        onChange={(event) =>
          onChange(event.target.value === "" ? null : Number(event.target.value))
        }
      />
    )}
  </Field>
);

const Tracking = ({ settings }: { settings: SendingSettingsDto }) => {
  const { atLeast } = useActive();
  const save = useSaveSendingSettings();
  const [form, setForm] = useState(settings);
  useEffect(() => setForm(settings), [settings]);
  const dirty = JSON.stringify(form) !== JSON.stringify(settings);
  const disabled = !atLeast("OWNER");

  return (
    <Panel
      title="Tracking, score and alerts"
      about="What we record, what counts toward the lead score, and when the team hears about it."
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted">Tracking</h3>
          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              disabled={disabled}
              checked={form.trackOpens}
              onChange={(event) => setForm({ ...form, trackOpens: event.target.checked })}
            />
            <span>
              Record opens
              <span className="block text-xs text-muted">
                Apple Mail loads images itself, so many opens aren't people. We keep them, label
                them, and only count the ones that look human. Some senders turn this off to help
                inbox placement.
              </span>
            </span>
          </label>
          <Field
            label="Link tracking domain"
            hint="Clicks go through this subdomain of the brand so links don't look like a mail tool's."
          >
            {(props) => (
              <Input
                {...props}
                disabled={disabled}
                placeholder={`go.${settings.senders[0]?.domain.name ?? "yourbrand.com"}`}
                value={form.trackingDomain ?? ""}
                onChange={(event) =>
                  setForm({ ...form, trackingDomain: event.target.value || null })
                }
              />
            )}
          </Field>
          <div className="rounded-md bg-paper/60 p-3 text-xs leading-relaxed text-muted">
            <p className="font-medium text-ink">Counted as a machine, never scored</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              <li>Opens loaded by Apple's privacy proxy</li>
              <li>A click within about two minutes of delivery</li>
              <li>Several links clicked in the same second</li>
              <li>A visit with no scrolling from a data-centre browser</li>
            </ul>
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted">
            Points per human event
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <NumberOrBlank
              label="Open"
              value={form.scoring.open}
              onChange={(value) =>
                setForm({ ...form, scoring: { ...form.scoring, open: value ?? 0 } })
              }
            />
            <NumberOrBlank
              label="Click"
              value={form.scoring.click}
              onChange={(value) =>
                setForm({ ...form, scoring: { ...form.scoring, click: value ?? 0 } })
              }
            />
            <NumberOrBlank
              label="Site visit"
              value={form.scoring.visit}
              onChange={(value) => setForm({ ...form, scoring: { ...form.scoring, visit: value } })}
            />
            <NumberOrBlank
              label="Reply"
              value={form.scoring.reply}
              onChange={(value) => setForm({ ...form, scoring: { ...form.scoring, reply: value } })}
            />
          </div>
          <p className="text-xs text-muted">
            Michael's weights: opens add 10 and clicks add 7, so someone who only opens scores in
            tens. Counts are kept separately too, so a score is never ambiguous.
          </p>
          <h3 className="pt-2 text-xs font-semibold uppercase tracking-widest text-muted">
            Alerts
          </h3>
          {(
            [
              ["reply", "A reply"],
              ["click", "A human click"],
              ["visit", "A human site visit"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="size-4"
                disabled={disabled}
                checked={form.alerts[key]}
                onChange={(event) =>
                  setForm({ ...form, alerts: { ...form.alerts, [key]: event.target.checked } })
                }
              />
              Tell the owner straight away about {label.toLowerCase()}
            </label>
          ))}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Weekly list on">
              {(props) => (
                <select
                  {...props}
                  className={selectClass}
                  disabled={disabled}
                  value={form.alerts.weeklyListDay}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      alerts: {
                        ...form.alerts,
                        weeklyListDay: event.target
                          .value as SendingSettingsDto["alerts"]["weeklyListDay"],
                      },
                    })
                  }
                >
                  {(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"] as const).map(
                    (day) => (
                      <option key={day}>{day}</option>
                    ),
                  )}
                </select>
              )}
            </Field>
            <Field label="Follow up after (days)">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  max={30}
                  disabled={disabled}
                  value={form.followUpAfterDays}
                  onChange={(event) =>
                    setForm({ ...form, followUpAfterDays: Number(event.target.value) || 1 })
                  }
                />
              )}
            </Field>
          </div>
        </div>
      </div>
      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      {dirty && !disabled && (
        <div className="flex gap-2">
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                trackOpens: form.trackOpens,
                trackingDomain: form.trackingDomain,
                scoring: form.scoring,
                alerts: form.alerts,
                followUpAfterDays: form.followUpAfterDays,
              })
            }
          >
            Save
          </Button>
          <Button variant="ghost" onClick={() => setForm(settings)}>
            Undo changes
          </Button>
        </div>
      )}
    </Panel>
  );
};

const Suppression = ({ settings }: { settings: SendingSettingsDto }) => {
  const { atLeast } = useActive();
  const suppress = useSuppress();
  const unsuppress = useUnsuppress();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    suppress.mutate(
      { email, reason },
      {
        onSuccess: () => {
          setEmail("");
          setReason("");
        },
      },
    );
  };
  return (
    <Panel
      title="Never contact"
      about="Unsubscribes, complaints and anyone who asked. Checked before every email, for every sender of this brand."
    >
      {atLeast("EDITOR") && (
        <form onSubmit={submit} className="flex flex-wrap gap-2">
          <Input
            aria-label="Email address"
            type="email"
            required
            placeholder="name@example.com"
            className="w-64"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Input
            aria-label="Reason"
            placeholder="Reason (optional)"
            className="min-w-48 flex-1"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button type="submit" variant="secondary" disabled={suppress.isPending}>
            Add
          </Button>
        </form>
      )}
      {suppress.isError && <ErrorText>{apiErrorMessage(suppress.error)}</ErrorText>}
      {settings.suppression.length === 0 ? (
        <p className="text-sm text-muted">Nobody yet.</p>
      ) : (
        <ul className={cn(card, "divide-y divide-line")}>
          {settings.suppression.map((row) => (
            <li
              key={row.email}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <span className="break-all text-ink">{row.email}</span>
              <span className="flex items-center gap-3 text-xs text-muted">
                {row.reason} · {formatDate(row.at)}
                {atLeast("OWNER") && (
                  <button
                    type="button"
                    aria-label={`Remove ${row.email}`}
                    onClick={() => unsuppress.mutate(row.email)}
                    className="text-muted hover:text-red-600"
                  >
                    <Trash2 className="size-4" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};
